import React, { useEffect, useState } from 'react';
import { withApollo } from 'react-apollo';
import { Link } from 'react-router-dom';
import { Alert, Button, Spin, Tag, Typography } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { lambda } from 'src/_shared/services/utils.js';
import {
	HEALTH_RANK,
	aggregateIntegrations,
	fetchCompanyConfigMap,
	fetchIntegrationsHealthFast,
	fetchLedgerRuns,
	healthStats,
} from './integrationsData.js';

const { Text } = Typography;

const HEALTH_TAG = {
	failed: { label: 'failing', color: 'red' },
	blocked: { label: 'blocked by breaker', color: 'magenta' },
	stale: { label: 'stale', color: 'gold' },
};

/**
 * Admin Console widget: one-glance integration fleet health with the problem
 * feeds named, linking to the full Admin Integrations console. Fetches once on
 * mount (no polling — the console is a landing page, not a monitor).
 */
const IntegrationsHealthCard = ({ client }) => {
	const [stats, setStats] = useState(null);
	const [problems, setProblems] = useState([]);
	const [dlqCount, setDlqCount] = useState(null);
	const [failedToLoad, setFailedToLoad] = useState(false);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				// Fast path: one lambda call; fall back to the browser-side scan.
				let integrations;
				let stats;
				const fast = await fetchIntegrationsHealthFast();
				if (fast) {
					integrations = fast.integrations;
					stats = fast.stats || healthStats(fast.integrations);
				} else {
					const [configsById, runs] = await Promise.all([
						fetchCompanyConfigMap(client),
						fetchLedgerRuns(client),
					]);
					integrations = aggregateIntegrations(runs, configsById);
					stats = healthStats(integrations);
				}
				if (cancelled) return;
				setStats(stats);
				setProblems(
					integrations
						.filter((row) => ['failed', 'blocked', 'stale'].includes(row.health))
						.sort(
							(a, b) =>
								(HEALTH_RANK[a.health] ?? 9) - (HEALTH_RANK[b.health] ?? 9) ||
								(a.companyName || '').localeCompare(b.companyName || '')
						)
				);
			} catch (error) {
				console.error('Integrations health card failed to load:', error);
				if (!cancelled) setFailedToLoad(true);
			}
			// Non-fatal by design: the health readout must not depend on the lambda.
			try {
				const res = await lambda({
					endpoint: 'mapping-config-api',
					variables: { action: 'queueHealth' },
				});
				const raw = res?.data ?? res;
				const payload = typeof raw === 'string' ? JSON.parse(raw) : raw;
				if (!cancelled && payload?.ok && Array.isArray(payload.queues)) {
					setDlqCount(payload.queues.reduce((n, q) => n + q.dlq, 0));
				}
			} catch (e) {
				// leave dlqCount unknown
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [client]);

	const shown = problems.slice(0, 5);

	return (
		<div className="custom-card">
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: 12,
				}}
			>
				<h5 style={{ marginBottom: 0 }}>Integrations</h5>
				<Link to="/admin-integrations">
					<Button type="primary">
						View Integrations Console <RightOutlined />
					</Button>
				</Link>
			</div>
			{failedToLoad ? (
				<Alert
					showIcon
					type="warning"
					message="Couldn't load integration health — open the console for the full view."
				/>
			) : !stats ? (
				<Spin />
			) : (
				<div>
					{/* Each chip deep-links into the console with the matching filter
					    applied (the console reads ?health= / ?activity= on mount). */}
					<div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
						<Link to="/admin-integrations" title="View in the integrations console">
							<Alert message={`${stats.total} integrations`} type="info" showIcon={false} />
						</Link>
						<Link to="/admin-integrations?health=failed" title="View failing feeds in the console">
							<Alert
								message={`${stats.failed} failing`}
								type={stats.failed ? 'error' : 'success'}
								showIcon
							/>
						</Link>
						<Link to="/admin-integrations?health=blocked" title="View breaker-blocked feeds in the console">
							<Alert
								message={`${stats.blocked} blocked by breaker`}
								type={stats.blocked ? 'error' : 'success'}
								showIcon
							/>
						</Link>
						<Link to="/admin-integrations?health=stale" title="View stale feeds in the console">
							<Alert
								message={`${stats.stale} stale`}
								type={stats.stale ? 'warning' : 'success'}
								showIcon
							/>
						</Link>
						{stats.running ? (
							<Link to="/admin-integrations?activity=running" title="View in-flight runs in the console">
								<Alert message={`${stats.running} running now`} type="info" showIcon />
							</Link>
						) : null}
						{dlqCount ? (
							<Link to="/admin-integrations" title="View the queue strip in the console">
								<Alert
									message={`${dlqCount} dead-letter — runs stuck after retries`}
									type="error"
									showIcon
								/>
							</Link>
						) : null}
					</div>
					{shown.length ? (
						<div>
							{shown.map((row) => (
								// Clicking a problem feed lands on the console with that
								// feed's run history drawer already open.
								<Link
									key={row.key}
									to={`/admin-integrations?history=${encodeURIComponent(row.key)}`}
									title="View this feed's run history"
									style={{ display: 'block', marginBottom: 4, color: 'inherit' }}
								>
									<Tag color={HEALTH_TAG[row.health]?.color}>
										{HEALTH_TAG[row.health]?.label}
									</Tag>
									<Text>
										{row.companyName} · {row.syncType}
									</Text>
									{row.cadenceLabel ? (
										<Text type="secondary" style={{ fontSize: 12 }}>
											{' '}
											({row.cadenceLabel})
										</Text>
									) : null}
								</Link>
							))}
							{problems.length > shown.length ? (
								<Text type="secondary" style={{ fontSize: 12 }}>
									…and {problems.length - shown.length} more —{' '}
									<Link to="/admin-integrations">see the console</Link>.
								</Text>
							) : null}
						</div>
					) : (
						<Text type="secondary">All enabled feeds are healthy.</Text>
					)}
				</div>
			)}
		</div>
	);
};

export default withApollo(IntegrationsHealthCard);
